import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, MessageCircle, ShieldCheck } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import { useLanguage } from '../contexts/LanguageContext';
import { getWhatsAppLink } from '../config/contact';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../utils/analytics';

const BookCallPage = () => {
  const { currentLang } = useLanguage();
  const copy = {
    en: {
      eyebrow: 'Project consultation',
      h1: 'Book a consultation with Webouse',
      intro:
        'Use this page to start a no-pressure project conversation. Webouse reviews the business goal, current website, required modules and delivery scope before confirming any paid work.',
      message: 'Hi Webouse, I would like to book a project consultation and discuss my website goals.',
      startWhatsApp: 'Start on WhatsApp',
      sendDetails: 'Send project details',
      coversTitle: 'What the consultation covers',
      points: [
        'Goals, audience and conversion path.',
        'Recommended package, modules and monthly care scope.',
        'Any privacy, integration or demo-access requirements.',
      ],
      note:
        'This page does not schedule a calendar event or submit payment. Webouse confirms every project directly.',
    },
    tr: {
      eyebrow: 'Proje danışmanlığı',
      h1: 'Webouse ile danışmanlık görüşmesi planla',
      intro:
        'Bu sayfa, baskı oluşturmayan bir proje görüşmesi başlatmak içindir. Webouse ücretli bir işi onaylamadan önce işletme hedefini, mevcut web sitesini, gereken modülleri ve teslim kapsamını inceler.',
      message: 'Merhaba Webouse, web sitesi hedeflerimi konuşmak için proje danışmanlığı görüşmesi planlamak istiyorum.',
      startWhatsApp: 'WhatsApp’ta Başlat',
      sendDetails: 'Proje detaylarını gönder',
      coversTitle: 'Görüşmede ele alınanlar',
      points: [
        'Hedefler, hedef kitle ve dönüşüm yolu.',
        'Önerilen paket, modüller ve aylık bakım kapsamı.',
        'Gizlilik, entegrasyon veya demo erişimi gereksinimleri.',
      ],
      note:
        'Bu sayfa takvim etkinliği oluşturmaz veya ödeme almaz. Webouse her projeyi doğrudan teyit eder.',
    },
  }[currentLang] || {
    eyebrow: 'Project consultation',
    h1: 'Book a consultation with Webouse',
    intro:
      'Use this page to start a no-pressure project conversation. Webouse reviews the business goal, current website, required modules and delivery scope before confirming any paid work.',
    message: 'Hi Webouse, I would like to book a project consultation and discuss my website goals.',
    startWhatsApp: 'Start on WhatsApp',
    sendDetails: 'Send project details',
    coversTitle: 'What the consultation covers',
    points: [
      'Goals, audience and conversion path.',
      'Recommended package, modules and monthly care scope.',
      'Any privacy, integration or demo-access requirements.',
    ],
    note:
      'This page does not schedule a calendar event or submit payment. Webouse confirms every project directly.',
  };

  return (
    <PublicPageShell mainClassName="pt-20">
      <section className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto grid max-w-[1200px] gap-10 px-5 py-16 md:px-[7.6923%] md:py-24 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
              {copy.eyebrow}
            </p>
            <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
              {copy.h1}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-white/64 md:text-lg">
              {copy.intro}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={getWhatsAppLink(copy.message)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackAnalyticsEvent(ANALYTICS_EVENTS.BOOK_CALL_STARTED, {
                    ctaLocation: 'book_call_hero_whatsapp',
                  })
                }
                className="btn-glass-primary"
              >
                <MessageCircle className="h-5 w-5" />
                {copy.startWhatsApp}
              </a>
              <Link to="/contact" className="btn-glass-secondary">
                {copy.sendDetails}
              </Link>
            </div>
          </div>

          <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-xl md:p-8">
            <CalendarCheck className="mb-5 h-8 w-8 text-[#00FFD1]" />
            <h2 className="text-2xl font-semibold text-white">
              {copy.coversTitle}
            </h2>
            <ul className="mt-5 space-y-4 text-sm leading-6 text-white/64">
              {copy.points.map((point) => (
                <li key={point} className="flex gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-[#00FFD1]" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-6 text-white/46">
              {copy.note}
            </p>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
};

export default BookCallPage;
